import { useEffect, useState, useCallback, Fragment } from "react";
import {
  Sparkles,
  RefreshCcw,
  TrendingDown,
  Plus,
  Copy,
  GitCompare,
  ChevronRight,
  ChevronDown,
  FileText,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  getDiffs,
  getDiffDetail,
  getDiffTotais,
  formatCNPJ,
  formatDateTime,
} from "@/lib/ocorenApi";
import { getCategoria } from "@/lib/categorias";

const TIPOS = [
  {
    key: "novas_nfs",
    label: "NFs novas",
    icon: Sparkles,
    color: "#10B981",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
  },
  {
    key: "status_alterado",
    label: "Status alterado",
    icon: RefreshCcw,
    color: "#3B82F6",
    bg: "bg-blue-50",
    text: "text-blue-700",
    border: "border-blue-200",
  },
  {
    key: "regressoes",
    label: "Regressões",
    icon: TrendingDown,
    color: "#F43F5E",
    bg: "bg-rose-50",
    text: "text-rose-700",
    border: "border-rose-200",
  },
  {
    key: "eventos_extras",
    label: "Eventos extras",
    icon: Plus,
    color: "#F59E0B",
    bg: "bg-amber-50",
    text: "text-amber-700",
    border: "border-amber-200",
  },
  {
    key: "duplicatas",
    label: "Duplicatas",
    icon: Copy,
    color: "#52525B",
    bg: "bg-zinc-100",
    text: "text-zinc-700",
    border: "border-zinc-200",
  },
];

const KPIMudanca = ({ tipo, total }) => {
  const Icon = tipo.icon;
  return (
    <div
      data-testid={`mudanca-kpi-${tipo.key}`}
      className="bg-white border border-[#E4E4E7] rounded-md p-5"
    >
      <div className="flex items-center gap-2">
        <Icon size={14} style={{ color: tipo.color }} />
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#52525B]">
          {tipo.label}
        </span>
      </div>
      <div className="font-heading text-3xl font-bold tracking-tight font-mono-data mt-3 text-[#09090B]">
        {total.toLocaleString("pt-BR")}
      </div>
    </div>
  );
};

const CategoriaBadge = ({ categoria }) => {
  const cat = getCategoria(categoria);
  const Icon = cat.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-sm border ${cat.bg} ${cat.text} ${cat.border}`}
    >
      <Icon size={9} />
      {cat.label}
    </span>
  );
};

const ChangeRow = ({ tipo, item, testId }) => {
  return (
    <div
      data-testid={testId}
      className={`p-3 rounded-md border ${tipo.bg} ${tipo.border} flex items-start gap-3`}
    >
      <tipo.icon size={14} style={{ color: tipo.color }} className="mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono-data font-bold text-[#09090B] text-sm">
            NF {item.numero_nota || "—"}
          </span>
          {item.serie && (
            <span className="text-[10px] font-mono-data text-[#52525B]">
              série {item.serie}
            </span>
          )}
          <span className="text-[10px] text-[#52525B]">•</span>
          <span className="text-xs font-mono-data text-[#52525B]">
            {formatCNPJ(item.cnpj_cliente)}
          </span>
        </div>

        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          {tipo.key === "novas_nfs" && (
            <>
              <CategoriaBadge categoria={item.categoria_nova} />
              <span className="text-xs text-[#09090B] truncate max-w-md">
                {item.descricao_nova || "—"}
              </span>
            </>
          )}
          {(tipo.key === "status_alterado" || tipo.key === "regressoes") && (
            <>
              <CategoriaBadge categoria={item.categoria_anterior} />
              <ArrowRight size={11} className="text-[#52525B]" />
              <CategoriaBadge categoria={item.categoria_nova} />
              <span className="text-xs text-[#52525B] truncate max-w-sm">
                {item.descricao_nova || "—"}
              </span>
            </>
          )}
          {tipo.key === "eventos_extras" && (
            <>
              <CategoriaBadge categoria={item.categoria} />
              <span className="text-xs text-[#09090B] truncate max-w-md">
                {item.descricao_nova || "—"}
              </span>
              <span className="text-[10px] text-[#52525B]">
                ({item.total_eventos_novos} evento(s) novo(s))
              </span>
            </>
          )}
          {tipo.key === "duplicatas" && (
            <>
              <CategoriaBadge categoria={item.categoria_atual} />
              <span className="text-xs text-[#52525B] truncate max-w-md">
                {item.descricao || "—"}
              </span>
              <span className="text-[10px] text-[#52525B]">
                ({item.total_duplicados} evento(s) já existente(s))
              </span>
            </>
          )}
        </div>

        <div className="mt-1 text-[10px] text-[#52525B] font-mono-data">
          {formatDateTime(item.data_evento)}
        </div>
      </div>
    </div>
  );
};

const DiffDetails = ({ arquivoId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getDiffDetail(arquivoId)
      .then(setData)
      .catch(() => toast.error("Falha ao carregar detalhes"))
      .finally(() => setLoading(false));
  }, [arquivoId]);

  if (loading)
    return (
      <div className="text-xs text-[#52525B] py-4" data-testid="diff-details-loading">
        Carregando detalhes…
      </div>
    );

  if (!data)
    return (
      <div className="text-xs text-[#52525B] py-4">Sem detalhes disponíveis.</div>
    );

  const sections = TIPOS.map((t) => ({ tipo: t, items: data[t.key] || [] })).filter(
    (s) => s.items.length > 0
  );

  if (sections.length === 0)
    return (
      <div className="text-xs text-[#52525B] py-4 text-center">
        Este arquivo não trouxe nenhuma mudança detectável (provavelmente é um
        reenvio idêntico).
      </div>
    );

  return (
    <div data-testid="diff-details" className="space-y-5">
      {sections.map(({ tipo, items }) => (
        <div key={tipo.key} data-testid={`diff-section-${tipo.key}`}>
          <div className="flex items-center gap-2 mb-2">
            <tipo.icon size={13} style={{ color: tipo.color }} />
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#52525B]">
              {tipo.label}
            </span>
            <span className="text-[10px] font-mono-data text-[#52525B]">
              ({items.length})
            </span>
          </div>
          <div className="space-y-2">
            {items.slice(0, 50).map((item, i) => (
              <ChangeRow
                key={`${tipo.key}-${i}`}
                tipo={tipo}
                item={item}
                testId={`diff-${tipo.key}-${i}`}
              />
            ))}
            {items.length > 50 && (
              <div className="text-xs text-[#52525B] text-center py-2">
                + {items.length - 50} item(ns) ocultos
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function Mudancas() {
  const [diffs, setDiffs] = useState([]);
  const [total, setTotal] = useState(0);
  const [totais, setTotais] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [visible, setVisible] = useState(100);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [d, t] = await Promise.all([getDiffs(10000), getDiffTotais()]);
      setDiffs(d.items || []);
      setTotal(d.total || 0);
      setTotais(t);
    } catch {
      toast.error("Falha ao carregar mudanças");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <div data-testid="page-mudancas" className="animate-fade-in-up">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#52525B] font-bold mb-2">
            Auditoria de Upload
          </div>
          <h1 className="font-heading text-4xl font-bold text-[#09090B] tracking-tight">
            Mudanças
          </h1>
          <p className="text-sm text-[#52525B] mt-2 max-w-2xl">
            O que cada arquivo OCOREN trouxe de novo: NFs adicionadas, status
            alterados, regressões críticas, eventos extras e duplicatas
            ignoradas.
          </p>
        </div>
      </div>

      {/* KPIs agregados */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        {TIPOS.map((t) => (
          <KPIMudanca
            key={t.key}
            tipo={t}
            total={totais ? totais[t.key] || 0 : 0}
          />
        ))}
      </div>

      {/* Lista de arquivos com diff */}
      <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E4E4E7] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] font-bold">
              Histórico
            </div>
            <h2 className="font-heading text-lg font-semibold text-[#09090B] mt-1">
              Diff por upload
            </h2>
          </div>
          <div className="text-xs text-[#52525B]">
            {total.toLocaleString("pt-BR")} arquivo(s) auditado(s)
            {visible < diffs.length && (
              <span className="ml-2 text-[#A1A1AA]">
                — exibindo {visible}
              </span>
            )}
          </div>
        </div>

        <table className="data-table w-full text-sm" data-testid="diffs-table">
          <thead>
            <tr>
              <th className="w-8"></th>
              <th>Arquivo</th>
              <th>Processado em</th>
              <th className="text-right">NFs afetadas</th>
              {TIPOS.map((t) => (
                <th key={t.key} className="text-right whitespace-nowrap">
                  <span className="inline-flex items-center gap-1">
                    <t.icon size={11} style={{ color: t.color }} />
                    {t.label}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={4 + TIPOS.length} className="text-center text-[#52525B] py-10">
                  Carregando…
                </td>
              </tr>
            )}
            {!loading && diffs.length === 0 && (
              <tr>
                <td colSpan={4 + TIPOS.length} className="text-center text-[#52525B] py-10">
                  Nenhum upload registrado ainda.
                </td>
              </tr>
            )}
            {!loading &&
              diffs.slice(0, visible).map((d, idx) => {
                const isExpanded = expanded === d.arquivo_id;
                const r = d.resumo || {};
                const hasChanges = TIPOS.some((t) => (r[t.key] || 0) > 0);
                return (
                  <Fragment key={d.arquivo_id}>
                    <tr
                      data-testid={`diff-row-${idx}`}
                      onClick={() => setExpanded(isExpanded ? null : d.arquivo_id)}
                      className="cursor-pointer"
                    >
                      <td className="w-8 text-[#52525B]">
                        {isExpanded ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )}
                      </td>
                      <td>
                        <div className="flex items-center gap-2 text-[#09090B]">
                          <FileText size={13} className="text-[#52525B]" />
                          <span className="font-medium font-mono-data text-xs">
                            {d.nome_arquivo}
                          </span>
                          {!hasChanges && (
                            <span className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-600 border border-zinc-200">
                              sem mudanças
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-xs text-[#52525B] font-mono-data whitespace-nowrap">
                        {formatDateTime(d.data_processamento)}
                      </td>
                      <td className="text-right font-mono-data font-semibold text-[#09090B]">
                        {r.nfs_afetadas || 0}
                      </td>
                      {TIPOS.map((t) => {
                        const v = r[t.key] || 0;
                        return (
                          <td
                            key={t.key}
                            className="text-right"
                            data-testid={`diff-cell-${t.key}-${idx}`}
                          >
                            {v > 0 ? (
                              <span
                                className={`inline-flex px-2 py-0.5 text-xs font-bold font-mono-data rounded-sm ${t.bg} ${t.text} border ${t.border}`}
                              >
                                {v}
                              </span>
                            ) : (
                              <span className="text-[#A1A1AA] font-mono-data text-xs">
                                0
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td
                          colSpan={4 + TIPOS.length}
                          className="bg-[#FAFAFA] p-6"
                        >
                          <DiffDetails arquivoId={d.arquivo_id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
          </tbody>
        </table>

        {!loading && visible < diffs.length && (
          <div className="px-5 py-4 border-t border-[#E4E4E7] flex items-center justify-center">
            <button
              data-testid="diffs-load-more"
              onClick={() => setVisible((v) => v + 200)}
              className="bg-white border border-[#E4E4E7] hover:border-[#09090B] text-[#09090B] px-4 py-2 text-sm font-medium rounded-md transition-colors"
            >
              Carregar mais ({diffs.length - visible} restante(s))
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
